import DSAuth from './DSAuth';
import {
	authErrorString,
	MissingAuthProtocolsError,
	PrimaryTokenFetchFailure,
} from '../AuthErrors';
import { AuthChallenge } from '../dsAuth/data/AuthChallenge';
import {
	HTTPFetchResponse,
	GenericRequest,
	AuthenticationMode,
	PrimaryTokenStatus,
} from './data/AuthTypes';
import TokenManager from '../TokenManager';
import { statusConstants, stringConstants, headerConstants } from '../AuthConstants';
import { logConstants } from '../../utils/Constants';
import AuthUtils from '../AuthUtils';
import { config } from '../AuthConfig';
import { Logger } from '../Logger';
import { PrimaryTokenServiceBase } from '../PrimaryTokenServiceBase';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import { AuthBridgeFactory } from '../../AccountConfig/bridge/auth/AuthBridgeFactory';

const authBridge = AuthBridgeFactory.getBridge();

interface AuthProtocolRequestResponse {
	readonly request: GenericRequest;
	readonly response: HTTPFetchResponse;
}

export default abstract class DsAuthPrimaryTokenServiceBase extends PrimaryTokenServiceBase {
	/**
	 * Process Primary token challenge response and fetch the primary token
	 *
	 * @param {HTTPFetchResponse} authChallengeResponse
	 * @param {string} referenceUri
	 * @param {string} id
	 * @param {string} transactionId
	 * @param {string} location
	 * @returns {Promise<void>}
	 * @memberof DsAuthPrimaryTokenServiceBase
	 */
	async getPrimaryTokenForURL(
		authChallengeResponse: HTTPFetchResponse,
		referenceUri: string,
		id: string,
		transactionId: string,
		location: string
	): Promise<PrimaryTokenStatus> {
		try {
			const authChallenge = await this.getAuthChallengeFromResponse(
				authChallengeResponse,
				id,
				transactionId,
				referenceUri
			);

			const { request, response } = await this.getAuthProtocols(
				id,
				transactionId,
				referenceUri,
				authChallenge,
				location
			);

			return await this.getToken(response, request, id, referenceUri, transactionId);
		} catch (e) {
			if (e instanceof PrimaryTokenFetchFailure) {
				throw e;
			}
			SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
			Logger.getInstance().write(
				logConstants.INFO,
				id + ':DSAUTH_PRIMARY_TOKEN_FETCH_FAIL:' + e.message
			);
			throw new PrimaryTokenFetchFailure(e);
		}
	}

	//#region AuthProtocols
	private async getAuthProtocols(
		id: string,
		transactionId: string,
		referenceUri: string,
		authChallenge: AuthChallenge,
		location: string
	): Promise<AuthProtocolRequestResponse> {
		// get auth protocols
		const authProtocolsRequest = this.prepareAuthProtocolsRequest(
			authChallenge,
			location,
			transactionId
		);
		const authProtocolsResponse = await this.fetchAuthProtocols(
			authProtocolsRequest,
			id,
			referenceUri
		);
		this.validateAuthProtocolsResponse(authProtocolsResponse, id);
		return {
			request: authProtocolsRequest,
			response: authProtocolsResponse,
		};
	}

	private prepareAuthProtocolsRequest(
		authChallenge: AuthChallenge,
		location: string,
		transactionId: string
	): GenericRequest {
		const authData = DSAuth.getInstance().prepareTokenRequest(
			authChallenge,
			this.getPrimaryTokenLifetime(),
			location,
			transactionId
		);
		return {
			method: stringConstants.POST,
			headers: authData.headers,
			body: authData.body,
			url: authChallenge.getLocations(),
		};
	}

	/// <summary>
	/// method returns auth protocols response. Example reponse body will contain:
	/// `<requesttokenchoices xmlns="http://citrix.com/delivery-services/1-0/auth/requesttokenchoices">
	///         <choices>
	///             <choice>
	///                 <protocol>webView</protocol>
	///                 <location>https://accounts-dsauthweb.cloud.com/ECzs9gI6kU-DF5FdvJZFVQ/ExplicitForms/Start</location>
	///             </choice>
	///         </choices>
	///         </requesttokenchoices>`
	/// </summary>
	private async fetchAuthProtocols(
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string
	): Promise<HTTPFetchResponse> {
		const { method, headers, body, url } = authProtocolsRequest;
		try {
			Logger.getInstance().write(
				logConstants.INFO,
				id + ':Getting primary token choices'
			);

			if (config.isUnifiedAuthenticationEnabled) {
				headers['Encode'] = 'False';
			}

			return await authBridge.authFetch(method, url, headers, body, referenceUri, false);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: DSAUTH_FETCH_AUTHPROTOCOLS_RESPONSE_FAILURE:${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}
	}

	private validateAuthProtocolsResponse(
		authProtocolsResponse: HTTPFetchResponse,
		id: string
	): void {
		const statusCode = authProtocolsResponse.statusCode;
		if (statusCode !== statusConstants['300']) {
			const errorMsg =
				id +
				': ' +
				authErrorString.DSAUTH_MISSING_PROTOCOLS +
				' StatusCode : ' +
				statusCode;
			Logger.getInstance().write(logConstants.ERROR, errorMsg);
			const innerError = new MissingAuthProtocolsError(errorMsg);
			throw new PrimaryTokenFetchFailure(innerError);
		}
	}
	//#endregion

	protected abstract getToken(
		authProtocolsResponse: HTTPFetchResponse,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<PrimaryTokenStatus>;

	protected abstract getPrimaryTokenLifetime(): string;

	//#region TokenRequest
	/**
	 * Send request to destroy the primary token on the server
	 *
	 * @param {string} token
	 * @param {string} serviceURL
	 * @param {string} referenceUri
	 * @returns {Promise<void>}
	 * @memberof PrimaryTokenService
	 */
	public async sendDestroyTokenRequest(
		token: string,
		serviceURL: string,
		referenceUri: string,
		id: string
	): Promise<void> {
		const authData = DSAuth.getInstance().prepareDestroyTokenRequest(token, id);
		authData.headers = AuthUtils.getInstance().addTokenToRequestHeader(
			authData.headers,
			token,
			AuthenticationMode.DSAuth
		);
		authData.headers['Connection'] = headerConstants.CLOSE;
		await authBridge.authFetch(
			stringConstants.POST,
			serviceURL,
			authData.headers,
			authData.body,
			referenceUri,
			false
		);
	}
	//#endregion

	/**
	 * Check if a primary token exists for the given service url
	 *
	 * @param {string} id
	 * @param {string} serviceURL
	 * @returns {Promise<boolean>}
	 * @memberof PrimaryTokenService
	 */
	public static async isLoggedOnToAMLService(
		id: string,
		serviceURL: string
	): Promise<boolean> {
		return await TokenManager.getInstance().hasCachedPrimaryTokenForServiceURL(
			id,
			serviceURL
		);
	}
}
